import React, { useEffect, useMemo, useState } from 'react';
import {
  FiEdit2,
  FiEye,
  FiRefreshCw,
  FiSearch,
  FiTrash2,
  FiUploadCloud,
  FiX,
} from 'react-icons/fi';
import { buildFileUrl } from '../../Services/api';
import useAPI from '../../hooks/useAPI';
import useDeleteQr_code from '../../hooks/useDeleteQr_code';
import usePostQr_code from '../../hooks/usePostQr_code';
import usePutQr_code from '../../hooks/useUpdateQr_code';
import endpointsAdmin from '../../Services/endpointAdmin';
import { extractArrayPayload, findFieldDeep, getAdminLembagaScope } from './adminScope';

const initialForm = {
  id_qrcode: '',
  id_lembaga: '',
  nama_qrcode: '',
  jenis_qrcode: '',
  image_qrcode: '',
  nominal_default: '',
  is_active: '1',
  qr_string: '',
  keterangan: '',
};

const requiredFieldProps = (message) => ({
  required: true,
  title: message,
  onInvalid: (event) => event.target.setCustomValidity(message),
  onInput: (event) => event.target.setCustomValidity(''),
});

const formatRupiah = (value) => {
  if (value === null || value === undefined || value === '') return 'Fleksibel';
  const num = Number(value);
  if (Number.isNaN(num) || num === 0) return 'Fleksibel';
  return new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
    minimumFractionDigits: 0,
  }).format(num);
};

function QrisAdmin({ admin }) {
  const { data: qrcodeData, loading, error, refetch } = useAPI(endpointsAdmin.qrcode.getAll);
  const { postQr_code, loading: creating, error: createError } = usePostQr_code();
  const { putQr_code, loading: updating, error: updateError } = usePutQr_code();
  const { deleteQr_code, error: deleteError } = useDeleteQr_code();
  const lembagaScope = useMemo(() => getAdminLembagaScope(admin), [admin]);

  const [form, setForm] = useState(initialForm);
  const [actionMessage, setActionMessage] = useState('');
  const [isEditing, setIsEditing] = useState(false);

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [viewMode, setViewMode] = useState('grid');
  const [previewModalItem, setPreviewModalItem] = useState(null);
  const [copiedStringId, setCopiedStringId] = useState('');

  const resolvedAdminLembagaId = useMemo(() => {
    if (lembagaScope.id) return String(lembagaScope.id).trim();

    try {
      const savedAdmin = localStorage.getItem('admin_user');
      if (!savedAdmin) return '';

      const parsedAdmin = JSON.parse(savedAdmin);
      return String(
        findFieldDeep(parsedAdmin, ['selected_id_lembaga', 'id_lembaga', 'idLembaga']) || ''
      ).trim();
    } catch (errorParsingAdmin) {
      return '';
    }
  }, [lembagaScope.id]);

  useEffect(() => {
    if (!resolvedAdminLembagaId) return;
    setForm((prev) => (
      prev.id_lembaga
        ? prev
        : {
            ...prev,
            id_lembaga: resolvedAdminLembagaId,
          }
    ));
  }, [resolvedAdminLembagaId]);

  const qrcodeList = useMemo(() => {
    const rawList = extractArrayPayload(qrcodeData, ['qrcode', 'data', 'result', 'rows']);
    if (!lembagaScope.active) return rawList;

    return rawList.filter((item) => {
      const itemLembagaId = String(
        findFieldDeep(item, ['id_lemabaga', 'id_lembaga', 'idLembaga']) || ''
      ).trim();
      return !itemLembagaId || itemLembagaId === resolvedAdminLembagaId;
    });
  }, [qrcodeData, lembagaScope.active, resolvedAdminLembagaId]);

  const stats = useMemo(() => {
    const total = qrcodeList.length;
    let active = 0;
    let inactive = 0;
    qrcodeList.forEach((item) => {
      const isActive = String(item.is_active ?? '1').trim() !== '0' && String(item.is_active).toLowerCase() !== 'false';
      if (isActive) active += 1;
      else inactive += 1;
    });
    return { total, active, inactive };
  }, [qrcodeList]);

  const filteredQrcodeList = useMemo(() => {
    return qrcodeList.filter((item) => {
      const name = String(item.nama_qrcode || item.nama || '').toLowerCase();
      const jenis = String(item.jenis_qrcode || item.jenis_qr || item.type || '').toLowerCase();
      const ket = String(item.keterangan || item.description || '').toLowerCase();
      const search = searchTerm.toLowerCase().trim();
      const matchesSearch = !search || name.includes(search) || jenis.includes(search) || ket.includes(search);

      const isActive = String(item.is_active ?? '1').trim() !== '0' && String(item.is_active).toLowerCase() !== 'false';
      let matchesStatus = true;
      if (statusFilter === 'active') matchesStatus = isActive;
      if (statusFilter === 'inactive') matchesStatus = !isActive;

      return matchesSearch && matchesStatus;
    });
  }, [qrcodeList, searchTerm, statusFilter]);

  const clearForm = () => {
    setForm({
      ...initialForm,
      id_lembaga: resolvedAdminLembagaId,
    });
    setIsEditing(false);
    setActionMessage('');
  };

  const handleChange = (event) => {
    const { name, value, files, type } = event.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'file' ? files?.[0] || '' : value,
    }));
  };

  const handleEdit = (item) => {
    setForm({
      id_qrcode: item.id_qrcode || item.id || '',
      id_lembaga: String(
        item.id_lembaga ?? item.id_lemabaga ?? item.idLembaga ?? resolvedAdminLembagaId ?? ''
      ).trim(),
      nama_qrcode: item.nama_qrcode || item.nama || '',
      jenis_qrcode: item.jenis_qrcode || item.jenis_qr || item.type || '',
      image_qrcode: item.image_qrcode || item.image || '',
      nominal_default: String(item.nominal_default ?? item.nominal ?? ''),
      is_active: item.is_active == null ? '1' : String(item.is_active),
      qr_string: String(item.qr_string || item.qrString || '').trim(),
      keterangan: item.keterangan || item.description || '',
    });
    setIsEditing(true);
    setActionMessage('');
    const formSection = document.getElementById('qris-form-section');
    if (formSection) {
      formSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!form.nama_qrcode.trim() || !form.jenis_qrcode.trim()) {
      setActionMessage('Nama QRIS dan jenis QRIS wajib diisi.');
      return;
    }

    if (!form.id_qrcode && !(form.image_qrcode instanceof File)) {
      setActionMessage('Gambar QR code wajib diupload saat membuat QRIS baru.');
      return;
    }

    const finalIdLembaga = String(form.id_lembaga || resolvedAdminLembagaId || '').trim();

    if (!finalIdLembaga) {
      setActionMessage('ID lembaga belum tersedia. Silakan login ulang dan pilih lembaga terlebih dahulu.');
      return;
    }

    const payload = {
      id_lembaga: finalIdLembaga,
      nama_qrcode: form.nama_qrcode,
      jenis_qrcode: form.jenis_qrcode,
      image_qrcode: form.image_qrcode instanceof File ? form.image_qrcode : '',
      nominal_default: form.nominal_default,
      is_active: form.is_active,
      qr_string: String(form.qr_string || '').trim(),
      keterangan: form.keterangan,
    };

    let result;

    if (form.id_qrcode) {
      result = await putQr_code(
        form.id_qrcode,
        payload.nama_qrcode,
        payload.jenis_qrcode,
        payload.image_qrcode,
        payload.nominal_default,
        payload.is_active,
        payload.id_lembaga,
        payload.qr_string,
        payload.keterangan
      );
    } else {
      result = await postQr_code(
        payload.nama_qrcode,
        payload.jenis_qrcode,
        payload.image_qrcode,
        payload.nominal_default,
        payload.is_active,
        payload.id_lembaga,
        payload.qr_string,
        payload.keterangan
      );
    }

    if (result) {
      await refetch();
      setActionMessage(form.id_qrcode ? 'QRIS berhasil diperbarui.' : 'QRIS berhasil ditambahkan.');
      clearForm();
    } else {
      setActionMessage(createError || updateError || 'Terjadi kesalahan saat menyimpan QRIS.');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Apakah Anda yakin ingin menghapus data QRIS ini?')) {
      return;
    }

    const deleted = await deleteQr_code(id);
    if (deleted) {
      await refetch();
      setActionMessage('QRIS berhasil dihapus.');
      if (form.id_qrcode === String(id)) {
        clearForm();
      }
    } else {
      setActionMessage(deleteError || 'Gagal menghapus QRIS.');
    }
  };

  const isItemActive = (value) => {
    const normalized = String(value ?? '1').trim();
    return normalized !== '0' && normalized.toLowerCase() !== 'false';
  };

  const displayLembaga = lembagaScope.name || (resolvedAdminLembagaId ? `Lembaga #${resolvedAdminLembagaId}` : '-');

  const imagePreview = useMemo(() => {
    if (form.image_qrcode instanceof File) {
      return URL.createObjectURL(form.image_qrcode);
    }

    if (typeof form.image_qrcode === 'string' && form.image_qrcode.trim()) {
      return buildFileUrl(form.image_qrcode);
    }

    return '';
  }, [form.image_qrcode]);

  useEffect(() => {
    if (!imagePreview || !(form.image_qrcode instanceof File)) return undefined;

    return () => {
      URL.revokeObjectURL(imagePreview);
    };
  }, [imagePreview, form.image_qrcode]);

  const getItemImageUrl = (item) => {
    const img = item.image_qrcode || item.image || '';
    if (!img) return '';
    if (typeof img === 'string' && img.trim()) {
      return buildFileUrl(img);
    }
    return '';
  };

  const handleCopyString = (text, id) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopiedStringId(id);
    setTimeout(() => setCopiedStringId(''), 2000);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 px-6 py-12">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-blue-200 border-t-blue-600" />
        <p className="mt-4 text-sm font-semibold text-slate-700">Memuat Data QRIS...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-6 py-12">
        <div className="w-full max-w-md rounded-2xl border border-red-200 bg-white p-6 shadow-sm text-center">
          <h2 className="text-lg font-bold text-slate-900">Gagal Memuat Data</h2>
          <p className="mt-2 text-sm text-slate-600">{error}</p>
          <button
            type="button"
            onClick={() => refetch()}
            className="mt-5 inline-flex items-center gap-2 rounded-xl bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-blue-700"
          >
            <FiRefreshCw /> Coba Lagi
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50/70 px-4 sm:px-6 lg:px-12 py-8">
      <div className="max-w-7xl mx-auto space-y-8">
        {/* Page Header */}
        <div className="rounded-2xl bg-slate-900 p-6 sm:p-8 text-white shadow-md">
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-white">
            Kelola QRIS
          </h1>
          <p className="mt-2 text-sm text-slate-300">
            Kelola daftar kode QRIS aktif, jenis metode transaksi, serta gambar QR untuk pembayaran donasi.
          </p>
        </div>

        {/* KPI Stats Cards - Minimalist without icon clutter */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Total QRIS</span>
            <p className="mt-2 text-3xl font-bold text-slate-900">{stats.total}</p>
            <p className="mt-1 text-xs text-slate-500">Terdaftar di sistem</p>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold uppercase tracking-wider text-slate-500">QRIS Aktif</span>
              <span className="h-2.5 w-2.5 rounded-full bg-emerald-500" />
            </div>
            <p className="mt-2 text-3xl font-bold text-emerald-600">{stats.active}</p>
            <p className="mt-1 text-xs text-slate-500">Siap digunakan donatur</p>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Non-Aktif</span>
              <span className="h-2.5 w-2.5 rounded-full bg-slate-400" />
            </div>
            <p className="mt-2 text-3xl font-bold text-slate-700">{stats.inactive}</p>
            <p className="mt-1 text-xs text-slate-500">Diarsipkan / Ditutup</p>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Lembaga</span>
            <p className="mt-2 text-base font-bold text-slate-900 truncate" title={displayLembaga}>
              {displayLembaga}
            </p>
            <p className="mt-1 text-xs text-slate-500">Scope akses aktif</p>
          </div>
        </div>

        {/* Action Message Alert */}
        {actionMessage && (
          <div className="flex items-center justify-between rounded-xl border border-blue-200 bg-blue-50 p-4 text-sm font-medium text-blue-900">
            <span>{actionMessage}</span>
            <button
              type="button"
              onClick={() => setActionMessage('')}
              className="text-blue-600 hover:text-blue-800 p-1"
            >
              <FiX />
            </button>
          </div>
        )}

        {/* Main Layout: Left List, Right Form */}
        <div className="grid gap-8 xl:grid-cols-[1.6fr_1fr]">
          {/* LEFT SECTION: QRIS LIST */}
          <section className="flex flex-col rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
            {/* Controls */}
            <div className="p-5 border-b border-slate-100 bg-slate-50/50 space-y-4">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h2 className="text-lg font-bold text-slate-900">Daftar QRIS</h2>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Menampilkan {filteredQrcodeList.length} dari {qrcodeList.length} QRIS
                  </p>
                </div>

                {/* View Switcher */}
                <div className="inline-flex rounded-lg bg-slate-200/70 p-1">
                  <button
                    type="button"
                    onClick={() => setViewMode('grid')}
                    className={`rounded-md px-3 py-1 text-xs font-bold transition ${
                      viewMode === 'grid' ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-600 hover:text-slate-900'
                    }`}
                  >
                    Grid
                  </button>
                  <button
                    type="button"
                    onClick={() => setViewMode('table')}
                    className={`rounded-md px-3 py-1 text-xs font-bold transition ${
                      viewMode === 'table' ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-600 hover:text-slate-900'
                    }`}
                  >
                    Tabel
                  </button>
                </div>
              </div>

              {/* Search & Filter */}
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                <div className="relative flex-1">
                  <FiSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 text-sm" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Cari nama atau jenis QRIS..."
                    className="w-full rounded-xl border border-slate-200 bg-white pl-9 pr-8 py-2 text-xs sm:text-sm text-slate-900 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  />
                  {searchTerm && (
                    <button
                      type="button"
                      onClick={() => setSearchTerm('')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      <FiX />
                    </button>
                  )}
                </div>

                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => setStatusFilter('all')}
                    className={`rounded-lg px-3 py-2 text-xs font-bold transition ${
                      statusFilter === 'all'
                        ? 'bg-blue-50 text-blue-700 border border-blue-200'
                        : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
                    }`}
                  >
                    Semua
                  </button>
                  <button
                    type="button"
                    onClick={() => setStatusFilter('active')}
                    className={`rounded-lg px-3 py-2 text-xs font-bold transition ${
                      statusFilter === 'active'
                        ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                        : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
                    }`}
                  >
                    Aktif
                  </button>
                  <button
                    type="button"
                    onClick={() => setStatusFilter('inactive')}
                    className={`rounded-lg px-3 py-2 text-xs font-bold transition ${
                      statusFilter === 'inactive'
                        ? 'bg-slate-100 text-slate-700 border border-slate-200'
                        : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
                    }`}
                  >
                    Non-Aktif
                  </button>
                </div>
              </div>
            </div>

            {/* List Body */}
            <div className="p-5 flex-1">
              {filteredQrcodeList.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-center text-slate-500">
                  <p className="text-sm font-semibold">Tidak ada data QRIS</p>
                  <p className="mt-1 text-xs text-slate-400">
                    {searchTerm || statusFilter !== 'all'
                      ? 'Tidak menemukan data yang cocok dengan pencarian.'
                      : 'Belum ada data QRIS yang tersimpan.'}
                  </p>
                </div>
              ) : viewMode === 'grid' ? (
                <div className="grid gap-4 sm:grid-cols-2">
                  {filteredQrcodeList.map((item) => {
                    const id = item.id_qrcode || item.id || '';
                    const active = isItemActive(item.is_active);
                    const imgUrl = getItemImageUrl(item);
                    const nominalText = formatRupiah(item.nominal_default);
                    const qrString = String(item.qr_string || item.qrString || '').trim();

                    return (
                      <div
                        key={id}
                        className={`flex flex-col justify-between rounded-2xl border p-4 transition-all ${
                          active
                            ? 'border-slate-200 bg-white hover:border-blue-300'
                            : 'border-slate-200 bg-slate-50/70'
                        }`}
                      >
                        <div>
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex items-center gap-3">
                              <div
                                onClick={() => imgUrl && setPreviewModalItem(item)}
                                className={`h-12 w-12 flex-shrink-0 cursor-pointer overflow-hidden rounded-xl border bg-slate-100 ${
                                  imgUrl ? 'border-slate-200 hover:border-blue-500' : 'border-dashed border-slate-300'
                                }`}
                              >
                                {imgUrl ? (
                                  <img
                                    src={imgUrl}
                                    alt={item.nama_qrcode || 'QR Code'}
                                    className="h-full w-full object-cover"
                                  />
                                ) : (
                                  <div className="flex h-full items-center justify-center text-[10px] font-bold text-slate-400">
                                    QR
                                  </div>
                                )}
                              </div>
                              <div className="overflow-hidden">
                                <h3 className="text-sm font-bold text-slate-900 truncate" title={item.nama_qrcode}>
                                  {item.nama_qrcode || item.nama || 'Tanpa Nama'}
                                </h3>
                                <p className="text-xs text-slate-500 truncate">
                                  {item.jenis_qrcode || item.jenis_qr || 'QRIS Standard'}
                                </p>
                              </div>
                            </div>

                            <span
                              className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${
                                active
                                  ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                                  : 'bg-slate-200 text-slate-600 border border-slate-300'
                              }`}
                            >
                              {active ? 'Aktif' : 'Non-aktif'}
                            </span>
                          </div>

                          <div className="mt-3 space-y-1 rounded-xl bg-slate-50 p-2.5 text-xs">
                            <div className="flex justify-between text-slate-600">
                              <span>Nominal:</span>
                              <span className="font-bold text-slate-900">{nominalText}</span>
                            </div>
                            {item.keterangan && (
                              <p className="text-slate-500 line-clamp-1 italic text-[11px]">
                                {item.keterangan}
                              </p>
                            )}
                          </div>

                          {qrString && (
                            <div className="mt-2.5 flex items-center justify-between rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs">
                              <span className="truncate text-slate-500 font-mono text-[11px] max-w-[150px]">{qrString}</span>
                              <button
                                type="button"
                                onClick={() => handleCopyString(qrString, id)}
                                className="ml-2 text-blue-600 hover:text-blue-800 text-[11px] font-bold"
                              >
                                {copiedStringId === id ? 'Tersalin!' : 'Salin'}
                              </button>
                            </div>
                          )}
                        </div>

                        <div className="mt-4 flex items-center justify-between border-t border-slate-100 pt-3 text-xs">
                          <button
                            type="button"
                            onClick={() => setPreviewModalItem(item)}
                            className="inline-flex items-center gap-1 text-slate-600 hover:text-blue-600 font-medium"
                          >
                            <FiEye /> lihat QR
                          </button>

                          <div className="flex items-center gap-3">
                            <button
                              type="button"
                              onClick={() => handleEdit(item)}
                              className="inline-flex items-center gap-1 text-slate-700 hover:text-blue-600 font-medium"
                            >
                              <FiEdit2 /> Edit
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDelete(id)}
                              className="inline-flex items-center gap-1 text-red-600 hover:text-red-800 font-medium"
                            >
                              <FiTrash2 /> Hapus
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="overflow-x-auto rounded-xl border border-slate-200">
                  <table className="w-full text-left text-xs sm:text-sm">
                    <thead className="bg-slate-50 border-b border-slate-200 text-slate-700 font-bold text-xs">
                      <tr>
                        <th className="px-4 py-3">QR</th>
                        <th className="px-4 py-3">Nama QRIS</th>
                        <th className="px-4 py-3">Jenis</th>
                        <th className="px-4 py-3">Nominal</th>
                        <th className="px-4 py-3">Status</th>
                        <th className="px-4 py-3 text-right">Aksi</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200 bg-white">
                      {filteredQrcodeList.map((item) => {
                        const id = item.id_qrcode || item.id || '';
                        const active = isItemActive(item.is_active);
                        const imgUrl = getItemImageUrl(item);
                        const nominalText = formatRupiah(item.nominal_default);

                        return (
                          <tr key={id} className="hover:bg-slate-50 transition">
                            <td className="px-4 py-2.5">
                              <div
                                onClick={() => imgUrl && setPreviewModalItem(item)}
                                className="h-10 w-10 cursor-pointer overflow-hidden rounded-lg border border-slate-200 bg-slate-100"
                              >
                                {imgUrl ? (
                                  <img
                                    src={imgUrl}
                                    alt={item.nama_qrcode || 'QR'}
                                    className="h-full w-full object-cover"
                                  />
                                ) : (
                                  <div className="flex h-full items-center justify-center text-[10px] text-slate-400 font-bold">QR</div>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-2.5">
                              <p className="font-bold text-slate-900">{item.nama_qrcode || item.nama || '-'}</p>
                              {item.keterangan && (
                                <p className="text-xs text-slate-500 truncate max-w-xs">{item.keterangan}</p>
                              )}
                            </td>
                            <td className="px-4 py-2.5 text-slate-600">
                              {item.jenis_qrcode || item.jenis_qr || '-'}
                            </td>
                            <td className="px-4 py-2.5 font-bold text-slate-900">{nominalText}</td>
                            <td className="px-4 py-2.5">
                              <span
                                className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${
                                  active
                                    ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                                    : 'bg-slate-100 text-slate-600 border border-slate-200'
                                }`}
                              >
                                {active ? 'Aktif' : 'Non-aktif'}
                              </span>
                            </td>
                            <td className="px-4 py-2.5 text-right">
                              <div className="flex items-center justify-end gap-3 text-xs">
                                <button
                                  type="button"
                                  onClick={() => setPreviewModalItem(item)}
                                  className="text-slate-600 hover:text-blue-600 font-medium"
                                >
                                  Lihat
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleEdit(item)}
                                  className="text-slate-600 hover:text-blue-600 font-medium"
                                >
                                  Edit
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleDelete(id)}
                                  className="text-red-600 hover:text-red-800 font-medium"
                                >
                                  Hapus
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </section>

          {/* RIGHT SECTION: FORM EDIT / ADD QRIS */}
          <section
            id="qris-form-section"
            className="flex flex-col rounded-2xl border border-slate-200 bg-white shadow-sm p-5 space-y-5 h-fit sticky top-6"
          >
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <div>
                <span className="text-xs font-bold text-blue-600 uppercase tracking-wider">
                  {isEditing ? 'Mode Edit' : 'Mode Tambah'}
                </span>
                <h2 className="text-lg font-bold text-slate-900 mt-0.5">
                  {isEditing ? 'Ubah Data QRIS' : 'Tambah QRIS Baru'}
                </h2>
              </div>
              {isEditing && (
                <button
                  type="button"
                  onClick={clearForm}
                  className="rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-bold text-slate-600 hover:bg-slate-100 transition"
                >
                  Buat Baru
                </button>
              )}
            </div>

            <form className="space-y-4" onSubmit={handleSubmit}>
              {/* Scope Lembaga */}
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1">
                  Scope Lembaga
                </label>
                <div className="rounded-xl border border-slate-200 bg-slate-100/70 px-3.5 py-2.5 text-xs font-semibold text-slate-700">
                  {displayLembaga}
                </div>
                <input type="hidden" name="id_lembaga" value={form.id_lembaga} />
              </div>

              {/* Nama QRIS */}
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1">
                  Nama QRIS <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="nama_qrcode"
                  value={form.nama_qrcode}
                  onChange={handleChange}
                  {...requiredFieldProps('Nama QRIS wajib diisi.')}
                  className="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-xs sm:text-sm text-slate-900 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  placeholder="Contoh: QRIS Bank Syariah Indonesia"
                />
              </div>

              {/* Jenis QRIS */}
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1">
                  Jenis QRIS <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="jenis_qrcode"
                  value={form.jenis_qrcode}
                  onChange={handleChange}
                  {...requiredFieldProps('Jenis QRIS wajib diisi.')}
                  className="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-xs sm:text-sm text-slate-900 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  placeholder="Contoh: BSI / Mandiri / BCA / Statis"
                />
              </div>

              {/* Nominal Default */}
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1">
                  Nominal Default (Rp)
                </label>
                <input
                  type="number"
                  name="nominal_default"
                  value={form.nominal_default}
                  onChange={handleChange}
                  className="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-xs sm:text-sm text-slate-900 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  placeholder="0 (Kosongkan jika nominal bebas)"
                />
              </div>

              {/* Status Toggle */}
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1">
                  Status Aktif
                </label>
                <div className="grid grid-cols-2 gap-2 rounded-xl border border-slate-200 bg-slate-100/70 p-1">
                  <button
                    type="button"
                    onClick={() => setForm((prev) => ({ ...prev, is_active: '1' }))}
                    className={`rounded-lg py-2 text-xs font-bold transition ${
                      String(form.is_active) === '1'
                        ? 'bg-white text-emerald-700 shadow-sm'
                        : 'text-slate-600 hover:text-slate-900'
                    }`}
                  >
                    Aktif
                  </button>
                  <button
                    type="button"
                    onClick={() => setForm((prev) => ({ ...prev, is_active: '0' }))}
                    className={`rounded-lg py-2 text-xs font-bold transition ${
                      String(form.is_active) === '0'
                        ? 'bg-white text-slate-800 shadow-sm'
                        : 'text-slate-600 hover:text-slate-900'
                    }`}
                  >
                    Non-Aktif
                  </button>
                </div>
              </div>

              {/* Gambar QR Code */}
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1">
                  Gambar QR Code {!isEditing && <span className="text-red-500">*</span>}
                </label>

                <div className="relative flex flex-col items-center justify-center rounded-xl border border-dashed border-slate-300 bg-slate-50/70 p-4 hover:bg-slate-100/70 transition">
                  <input
                    type="file"
                    name="image_qrcode"
                    accept="image/*"
                    onChange={handleChange}
                    required={!isEditing}
                    title="Gambar QR code wajib diupload."
                    onInvalid={(event) => event.target.setCustomValidity('Gambar QR code wajib diupload.')}
                    onInput={(event) => event.target.setCustomValidity('')}
                    className="absolute inset-0 cursor-pointer opacity-0"
                  />
                  <FiUploadCloud className="text-2xl text-slate-400" />
                  <p className="mt-1.5 text-xs font-bold text-slate-700">
                    Klik atau tarik gambar QR ke sini
                  </p>
                  <p className="text-[11px] text-slate-500">PNG, JPG, JPEG (Max. 5MB)</p>
                </div>

                {imagePreview && (
                  <div className="mt-3 relative overflow-hidden rounded-xl border border-slate-200 bg-white p-2.5">
                    <img
                      src={imagePreview}
                      alt="Preview QR Code"
                      className="max-h-40 w-full rounded-lg object-contain bg-slate-50"
                    />
                  </div>
                )}
              </div>

              {/* QR String */}
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1">
                  QR String (Payload Code)
                </label>
                <input
                  type="text"
                  name="qr_string"
                  value={form.qr_string}
                  onChange={handleChange}
                  className="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-xs font-mono text-slate-900 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  placeholder="Opsional (contoh: 00020101021126...)"
                />
              </div>

              {/* Keterangan */}
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1">
                  Keterangan Tambahan
                </label>
                <textarea
                  name="keterangan"
                  value={form.keterangan}
                  onChange={handleChange}
                  rows="2"
                  className="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-xs text-slate-900 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  placeholder="Catatan tambahan..."
                />
              </div>

              {/* Submit / Cancel Buttons */}
              <div className="pt-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-end">
                <button
                  type="button"
                  onClick={clearForm}
                  className="rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-xs font-bold text-slate-700 hover:bg-slate-100 transition"
                >
                  Batal
                </button>

                <button
                  type="submit"
                  disabled={creating || updating}
                  className="inline-flex items-center justify-center rounded-xl bg-blue-600 px-5 py-2.5 text-xs font-bold text-white shadow-sm hover:bg-blue-700 disabled:opacity-70"
                >
                  {creating || updating ? 'Menyimpan...' : isEditing ? 'Simpan Perubahan' : 'Buat QRIS Baru'}
                </button>
              </div>

              {(createError || updateError || deleteError) && (
                <p className="text-xs font-semibold text-red-600">{createError || updateError || deleteError}</p>
              )}
            </form>
          </section>
        </div>
      </div>

      {/* QR LIGHTBOX MODAL */}
      {previewModalItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-4 backdrop-blur-sm">
          <div className="relative w-full max-w-md overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-slate-100 bg-slate-900 px-5 py-3.5 text-white">
              <h3 className="text-base font-bold text-white">{previewModalItem.nama_qrcode || 'QRIS Code'}</h3>
              <button
                type="button"
                onClick={() => setPreviewModalItem(null)}
                className="rounded-lg p-1 text-slate-300 hover:text-white transition"
              >
                <FiX className="text-lg" />
              </button>
            </div>

            <div className="p-5 space-y-4">
              <div className="flex justify-center rounded-xl border border-slate-200 bg-slate-50 p-4">
                {getItemImageUrl(previewModalItem) ? (
                  <img
                    src={getItemImageUrl(previewModalItem)}
                    alt="QR Code"
                    className="max-h-64 w-full object-contain rounded-lg"
                  />
                ) : (
                  <p className="py-8 text-xs font-semibold text-slate-400">Tidak ada gambar QR</p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="rounded-lg bg-slate-50 p-2.5">
                  <span className="text-slate-500">Jenis:</span>
                  <p className="font-bold text-slate-900">{previewModalItem.jenis_qrcode || '-'}</p>
                </div>
                <div className="rounded-lg bg-slate-50 p-2.5">
                  <span className="text-slate-500">Nominal Default:</span>
                  <p className="font-bold text-slate-900">{formatRupiah(previewModalItem.nominal_default)}</p>
                </div>
              </div>

              {previewModalItem.qr_string && (
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-2.5 text-xs">
                  <div className="flex items-center justify-between text-slate-500 mb-1">
                    <span>QR String:</span>
                    <button
                      type="button"
                      onClick={() => handleCopyString(previewModalItem.qr_string, 'modal')}
                      className="text-blue-600 font-bold"
                    >
                      {copiedStringId === 'modal' ? 'Tersalin!' : 'Salin'}
                    </button>
                  </div>
                  <p className="font-mono text-slate-800 break-all bg-white p-2 rounded border border-slate-200 text-[11px]">
                    {previewModalItem.qr_string}
                  </p>
                </div>
              )}
            </div>

            <div className="border-t border-slate-100 bg-slate-50 px-5 py-3 flex justify-end">
              <button
                type="button"
                onClick={() => setPreviewModalItem(null)}
                className="rounded-lg bg-slate-900 px-4 py-2 text-xs font-bold text-white hover:bg-slate-800 transition"
              >
                Tutup
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default QrisAdmin;
